import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  LineSegments,
  LineBasicMaterial,
  Matrix4,
  Vector3,
  ArrowHelper,
  Object3D,
  SkeletonHelper,
  Bone,
} from "three";

import { SkeletalStructure } from "../types/statsApi";
import { Axes, JointPositions } from "../types";
import { Basis } from "./util";

import { GeneralConstraint } from "../ik/constraint/general";
import { Joint } from "../ik/joint";

export interface ColorPool {
  default: Color[];
  [key: string]: Color[];
}

const defaultColorPool = {
  default: [new Color(0xff0000), new Color(0x68ff00)],
};

export class PointSkeleton extends LineSegments {
  structure: { [key: string]: string[] };
  points: JointPositions;

  private delta?: Vector3;

  constructor(
    skeletalStructure: { [key: string]: string[] },
    colorPool?: ColorPool
  ) {
    const geometry = new BufferGeometry();

    const vertices: number[] = [];
    const vColors: number[] = [];

    if (!colorPool) {
      colorPool = defaultColorPool;
    }

    for (const parent in skeletalStructure) {
      const joint = skeletalStructure[parent];
      let colors: Color[];

      for (const child of joint) {
        if (colorPool.hasOwnProperty(child)) {
          colors = colorPool[child];
        } else {
          colors = colorPool["default"];
        }

        if (colors.length != 2) {
          console.error("insufficient colors");
          colors = colorPool["default"];
        }

        const color1 = colors[0];
        const color2 = colors[1];
        vertices.push(0, 0, 0);
        vertices.push(0, 0, 0);
        vColors.push(color1.r, color1.g, color1.b);
        vColors.push(color2.r, color2.g, color2.b);
      }
    }

    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));
    geometry.setAttribute("color", new Float32BufferAttribute(vColors, 3));

    const material = new LineBasicMaterial({
      vertexColors: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      transparent: true,
      linewidth: 5,
    });

    super(geometry, material);

    this.type = "PointSkeleton";
    this.matrix = new Matrix4();
    this.matrixAutoUpdate = false;

    this.frustumCulled = false;

    this.structure = skeletalStructure;
    this.points = new JointPositions();
  }

  update(points: JointPositions, updateGL = true) {
    const geometry = this.geometry;
    const position = geometry.getAttribute("position");

    if (updateGL) {
      let offset = 0;
      for (const parent in this.structure) {
        const joint = this.structure[parent];

        for (const child of joint) {
          // TODO: add smoothing / filtering here
          // TODO: check that previous value is retained
          const parentTarget = points.get(parent);
          const childTarget = points.get(child);
          if (parentTarget) {
            position.setXYZ(
              offset,
              parentTarget.x + (this.delta ? this.delta.x : 0),
              parentTarget.y + (this.delta ? this.delta.y : 0),
              parentTarget.z + (this.delta ? this.delta.z : 0)
            );
          }
          if (childTarget) {
            position.setXYZ(
              offset + 1,
              childTarget.x + (this.delta ? this.delta.x : 0),
              childTarget.y + (this.delta ? this.delta.y : 0),
              childTarget.z + (this.delta ? this.delta.z : 0)
            );
          }
          offset += 2;
        }
      }
      this.updateMatrixWorld(true);
    }

    this.points = points;
  }

  setDelta(delta: Vector3) {
    if (!this.delta) {
      this.delta = new Vector3();
    }
    this.delta.copy(delta);
  }

  updateMatrixWorld(force: boolean) {
    this.geometry.getAttribute("position").needsUpdate = true;
    super.updateMatrixWorld(force);
  }
}

const colorExtent = new Color(0xff0000);

export class SwingExtents extends LineSegments {
  constraint: GeneralConstraint;
  points: Vector3[];

  constructor(constraint: GeneralConstraint) {
    const geometry = new BufferGeometry();

    const material = new LineBasicMaterial({
      color: colorExtent,
      vertexColors: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      transparent: true,
    });

    super(geometry, material);

    this.type = "SwingExtents";
    this.matrix = new Matrix4();
    this.matrixAutoUpdate = false;

    this.frustumCulled = false;

    this.constraint = constraint;
    this.points = [];
  }

  update() {
    this.points = [];
    this.constraint.getSwingExtent(this.points);

    this.geometry.setFromPoints(this.points);
    this.updateMatrixWorld(true);
  }

  updateMatrixWorld(force: boolean) {
    if (this.geometry.getAttribute("position")) {
      this.geometry.getAttribute("position").needsUpdate = true;
    }
    super.updateMatrixWorld(force);
  }
}

export const debugColorPool: ColorPool = {
  default: [new Color(0xe9a728), new Color(0xd20c29)],
  __kabsch: [new Color(0xaad935), new Color(0x75773d)],
  __orphan: [new Color(0x5f0f40), new Color(0xdc1766)],
};

export class ArmatureSkeleton extends Object3D {
  root: Joint;
  skel: PointSkeleton;
  axesHelpers: { [key: string]: AxesHelper } = {};
  swingHelpers: SwingExtents[] = [];

  initialised = false;

  colorPool: ColorPool = {
    default: [new Color(0x0013ff), new Color(0xfb00ff)],
    __kabsch: [new Color(0x47a17c), new Color(0x0000ff)],
    __orphan: [new Color(0xd27462), new Color(0xfb3987)],
  };

  constructor(root: Joint, colorPool?: ColorPool) {
    super();

    if (
      colorPool &&
      colorPool.hasOwnProperty("__kabsch") &&
      colorPool.hasOwnProperty("__orphan")
    ) {
      this.colorPool = colorPool;
    }

    const joints: Joint[] = [];
    root.getJoints(joints);
    joints.forEach((j) => {
      if (j.isKabschChild()) {
        this.colorPool[j.name] = this.colorPool.__kabsch;
      } else if (j.isOrphaned() && !j.isLeafEffector()) {
        this.colorPool[j.name] = this.colorPool.__orphan;
      }
    });

    this.root = root;
    const structure: SkeletalStructure = {};
    root.getStructure(structure);

    this.skel = new PointSkeleton(structure, this.colorPool);

    const positions = new JointPositions();
    root.getPoints(positions);
    this.skel.update(positions, true);

    const basies: { [key: string]: Basis } = {};
    root.getBasies(basies);
    for (const name in basies) {
      const helper = new AxesHelper(basies[name], 0.5);
      this.add(helper);
      this.axesHelpers[name] = helper;
    }

    const constraints: GeneralConstraint[] = [];
    root.getConstraints(constraints);
    for (const constraint of constraints) {
      const helper = new SwingExtents(constraint);
      this.add(helper);
      this.swingHelpers.push(helper);
    }

    this.frustumCulled = false;

    this.add(this.skel);
  }

  update() {
    if (!this.visible) {
      return;
    }

    const posedJoints: JointPositions = new JointPositions();
    this.root.getPoints(posedJoints);

    this.skel.update(posedJoints, true);

    for (const name in this.axesHelpers) {
      if (
        !this.initialised ||
        (this.visible && this.axesHelpers[name].visible)
      ) {
        this.axesHelpers[name].update();
      }
    }

    for (const swingHelper of this.swingHelpers) {
      if (!this.initialised || (this.visible && swingHelper.visible)) {
        swingHelper.update();
      }
    }

    if (!this.initialised) {
      this.initialised = true;
    }
  }
}

export class AxesHelper extends Object3D {
  arrows: ArrowHelper[];
  axes: Axes;

  constructor(axes: Axes, length = 1) {
    super();

    this.axes = axes;

    this.position.copy(axes.pos);

    this.arrows = [
      new ArrowHelper(
        axes.fwd,
        undefined,
        length,
        0x229954,
        undefined,
        0.1 * length
      ),
      new ArrowHelper(
        axes.up,
        undefined,
        length,
        0xcb4335,
        undefined,
        0.1 * length
      ),
      new ArrowHelper(
        axes.right,
        undefined,
        length,
        0x2e86c1,
        undefined,
        0.1 * length
      ),
    ];

    for (const arrow of this.arrows) {
      this.add(arrow);
    }
  }

  update() {
    this.position.copy(this.axes.pos);
    this.arrows[0].setDirection(this.axes.fwd);
    this.arrows[1].setDirection(this.axes.up);
    this.arrows[2].setDirection(this.axes.right);
  }
}

const _vector = new Vector3();
const _boneMatrix = new Matrix4();
const _matrixWorldInv = new Matrix4();

export class SkeletonHelperGlobal extends SkeletonHelper {
  parents: { [key: string]: Bone };

  constructor(object: Object3D) {
    super(object);

    this.parents = {};

    // cache parents to make it still work after flattening
    for (const bone of this.bones) {
      if (bone.parent && bone.parent.type == "Bone") {
        this.parents[bone.name] = bone.parent as Bone;
      }
    }
  }
  updateMatrixWorld(force: boolean) {
    const geometry = this.geometry;
    const position = geometry.getAttribute("position");

    _matrixWorldInv.copy(this.root.matrixWorld).invert();

    let j = 0;
    for (const bone of this.bones) {
      if (this.parents.hasOwnProperty(bone.name)) {
        _boneMatrix.multiplyMatrices(_matrixWorldInv, bone.matrixWorld);
        _vector.setFromMatrixPosition(_boneMatrix);
        position.setXYZ(j, _vector.x, _vector.y, _vector.z);

        const parent = this.parents[bone.name];
        _boneMatrix.multiplyMatrices(_matrixWorldInv, parent.matrixWorld);
        _vector.setFromMatrixPosition(_boneMatrix);
        position.setXYZ(j + 1, _vector.x, _vector.y, _vector.z);

        j += 2;
      }
    }

    geometry.getAttribute("position").needsUpdate = true;

    LineSegments.prototype.updateMatrixWorld.call(this, force);
  }
}
